import React, { useState } from 'react';
import { useLoanArrears, useLoanArrearDetail, useFixErroneousLoans } from '../hooks/useLoanArrears.ts';
import { toast } from 'react-hot-toast';
import Table from '../components/Table.tsx';
import Modal from '../components/Modal.tsx';
import { AlertTriangle, Download, RefreshCw, Eye, TrendingDown, DollarSign, Users, AlertCircle } from 'lucide-react';
import { PermissionPage } from '../components/PermissionGate';
import { PERMISSIONS } from '../utils/permissions';

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-KE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
}

function formatDate(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB');
}

function StatusBadge({ status, isErroneous }) {
  if (isErroneous) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
        <AlertCircle size={12} className="mr-1" />
        Erroneous
      </span>
    );
  }

  const statusConfig = {
    ACTIVE: { color: 'bg-yellow-100 text-yellow-800', text: 'Active' },
    COMPLETED: { color: 'bg-green-100 text-green-800', text: 'Completed' },
    DEFAULTER: { color: 'bg-red-100 text-red-800', text: 'Defaulter' },
  };

  const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', text: status };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      {config.text}
    </span>
  );
}

const LoanArrearsPage = () => {
  const [sortBy, setSortBy] = useState('balance_overdue');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedLoanId, setSelectedLoanId] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage] = useState(25);

  const { data: arrearsData, isLoading } = useLoanArrears({
    sort_by: sortBy,
    sort_order: sortOrder,
  });

  const { data: loanDetail, isLoading: isLoadingDetail } = useLoanArrearDetail(selectedLoanId);
  const fixErroneousLoans = useFixErroneousLoans();

  const handleViewDetails = (loanId) => {
    setSelectedLoanId(loanId);
    setShowDetailModal(true);
  };

  const handleFixErroneous = () => {
    if (window.confirm('This will recalculate and fix all erroneous loans. Continue?')) {
      fixErroneousLoans.mutate(undefined, {
        onSuccess: (data) => {
          toast.success(`Fixed ${data.summary.fixed_count} erroneous loan(s)`);
        },
        onError: (error) => {
          toast.error('Failed to fix erroneous loans');
        },
      });
    }
  };

  const handleExport = () => {
    if (!arrearsData?.data) return;

    const csvData = [
      ['Name', 'Client No.', 'ID No.', 'Mobile No.', 'Status', 'Balance Not Due', 'Balance Over Due', 'Total Balance', 'Credit Limit'],
      ...arrearsData.data.map(loan => [
        loan.member.name,
        loan.member.member_no,
        loan.member.id_no || '',
        loan.member.phone_number,
        loan.is_erroneous ? 'ERRONEOUS' : loan.status,
        loan.balance_not_due,
        loan.balance_overdue,
        loan.total_balance,
        '', // Credit limit not in loan data
      ])
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `loan-arrears-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Exported successfully');
  };

  const columns = [
    {
      label: 'Name',
      field: 'member.name',
      sortable: true,
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => (
        <div>
          <div className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer" onClick={() => handleViewDetails(row.loan_id)}>
            {row.member.name}
          </div>
          <div className="text-xs text-gray-500">{row.loan_no}</div>
        </div>
      ),
    },
    {
      label: 'Client No.',
      field: 'member.member_no',
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm text-gray-700',
      render: (row) => row.member.member_no,
    },
    {
      label: 'ID No.',
      field: 'member.id_no',
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm text-gray-700',
      render: (row) => row.member.id_no || '-',
    },
    {
      label: 'Mobile No.',
      field: 'member.phone_number',
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm text-gray-700',
      render: (row) => row.member.phone_number,
    },
    {
      label: 'Status',
      field: 'status',
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => <StatusBadge status={row.status} isErroneous={row.is_erroneous} />,
    },
    {
      label: 'Balance Not Due',
      field: 'balance_not_due',
      sortable: true,
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm text-right',
      render: (row) => formatCurrency(row.balance_not_due),
    },
    {
      label: 'Balance Over Due',
      field: 'balance_overdue',
      sortable: true,
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm text-right',
      render: (row) => (
        <span className={row.balance_overdue > 0 ? 'text-red-600 font-semibold' : ''}>
          {formatCurrency(row.balance_overdue)}
        </span>
      ),
    },
    {
      label: 'Total Balance',
      field: 'total_balance',
      sortable: true,
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm text-right font-semibold',
      render: (row) => formatCurrency(row.total_balance),
    },
    {
      label: 'Actions',
      field: 'actions',
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => (
        <button
          onClick={() => handleViewDetails(row.loan_id)}
          className="text-blue-600 hover:text-blue-800"
          title="View Details"
        >
          <Eye size={16} />
        </button>
      ),
    },
  ];

  const statsCards = [
    {
      label: 'Total Defaulters',
      value: arrearsData?.summary?.total_defaulters || 0,
      icon: Users,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
    },
    {
      label: 'Total Arrears Amount',
      value: `KES ${formatCurrency(arrearsData?.summary?.total_arrears_amount || 0)}`,
      icon: DollarSign,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
    {
      label: 'Principal at Risk',
      value: `KES ${formatCurrency(arrearsData?.summary?.total_principal_at_risk || 0)}`,
      icon: TrendingDown,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      label: 'Erroneous Loans',
      value: arrearsData?.summary?.erroneous_loans || 0,
      icon: AlertCircle,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
    },
  ];

  // Paginate data client-side
  const paginatedData = React.useMemo(() => {
    const allData = arrearsData?.data || [];
    const startIndex = (page - 1) * perPage;
    const endIndex = startIndex + perPage;
    return allData.slice(startIndex, endIndex);
  }, [arrearsData?.data, page, perPage]);

  return (
    <PermissionPage permission={PERMISSIONS.VIEW_ALL_LOANS}>
      <div className="min-h-screen">
      <div className="container mx-auto px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          <div className="flex items-center space-x-3">
            <AlertTriangle size={28} className="text-red-600" />
            <h2 className="text-2xl font-bold">Defaulters (Clients)</h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {arrearsData?.summary?.erroneous_loans > 0 && (
              <button
                onClick={handleFixErroneous}
                disabled={fixErroneousLoans.isPending}
                className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400"
              >
                <RefreshCw size={16} className="mr-2" />
                {fixErroneousLoans.isPending ? 'Fixing...' : 'Fix Erroneous'}
              </button>
            )}
            <button
              onClick={handleExport}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Download size={16} className="mr-2" />
              Export
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 kpi-grid">
          {statsCards.map((card, idx) => (
            <div key={idx} className={`${card.bgColor} rounded-xl shadow p-6`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">{card.label}</p>
                <card.icon size={24} className={card.color} />
              </div>
              <h3 className={`text-xl font-bold ${card.color}`}>{card.value}</h3>
            </div>
          ))}
        </div>

        {/* Arrears Table */}
        <div className="bg-white rounded-xl shadow">
          <Table
            columns={columns}
            data={paginatedData}
            loading={isLoading}
            page={page}
            perPage={perPage}
            total={arrearsData?.data?.length || 0}
            onPageChange={setPage}
          />
        </div>

        {/* Loan Detail Modal */}
        <Modal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedLoanId(null);
          }}
          title="Loan Arrears Details"
        >
          {isLoadingDetail ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : loanDetail?.data ? (
            <div className="space-y-6">
              {/* Member Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-3">Member Information</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Name:</span>
                    <p className="font-medium">{loanDetail.data.member.name}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Member No:</span>
                    <p className="font-medium">{loanDetail.data.member.member_no}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Phone:</span>
                    <p className="font-medium">{loanDetail.data.member.phone_number}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">ID No:</span>
                    <p className="font-medium">{loanDetail.data.member.id_no || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Loan Info */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-3">Loan Information</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Loan No:</span>
                    <p className="font-medium">{loanDetail.data.loan_no}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Loan Type:</span>
                    <p className="font-medium">{loanDetail.data.loan_type}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Principal:</span>
                    <p className="font-medium">KES {formatCurrency(loanDetail.data.principal)}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Total Amount:</span>
                    <p className="font-medium">KES {formatCurrency(loanDetail.data.total_amount)}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Total Repaid:</span>
                    <p className="font-medium text-green-600">KES {formatCurrency(loanDetail.data.total_repaid)}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Arrears Amount:</span>
                    <p className="font-medium text-red-600">KES {formatCurrency(loanDetail.data.arrears_amount)}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Start Date:</span>
                    <p className="font-medium">{formatDate(loanDetail.data.start_date)}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">End Date:</span>
                    <p className="font-medium">{formatDate(loanDetail.data.end_date)}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Days Overdue:</span>
                    <p className="font-medium text-red-600">{loanDetail.data.days_overdue}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <p className="font-medium">
                      <StatusBadge status={loanDetail.data.status} isErroneous={loanDetail.data.is_erroneous} />
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Schedule */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Payment Schedule</h3>
                <div className="overflow-x-auto">
                  <table className="responsive-table min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Expected</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Paid</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Shortfall</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {loanDetail.data.payment_schedule.map((payment) => (
                        <tr key={payment.month} className={payment.is_overdue ? 'bg-red-50' : ''}>
                          <td data-label="Month" className="px-3 py-2 text-sm">{payment.month}</td>
                          <td data-label="Date" className="px-3 py-2 text-sm">{formatDate(payment.payment_date)}</td>
                          <td data-label="Expected" className="px-3 py-2 text-sm text-right">{formatCurrency(payment.expected_amount)}</td>
                          <td data-label="Paid" className="px-3 py-2 text-sm text-right">{formatCurrency(payment.paid_amount)}</td>
                          <td data-label="Shortfall" className="px-3 py-2 text-sm text-right text-red-600">{formatCurrency(payment.shortfall)}</td>
                          <td data-label="Status" className="px-3 py-2 text-sm text-center">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              payment.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {payment.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Repayment History */}
              {loanDetail.data.repayment_history && loanDetail.data.repayment_history.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-3">Repayment History</h3>
                  <div className="overflow-x-auto">
                    <table className="responsive-table min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {loanDetail.data.repayment_history.map((repayment) => (
                          <tr key={repayment.id}>
                            <td data-label="Date" className="px-3 py-2 text-sm">{formatDate(repayment.payment_date)}</td>
                            <td data-label="Amount" className="px-3 py-2 text-sm text-right font-medium">{formatCurrency(repayment.amount)}</td>
                            <td data-label="Method" className="px-3 py-2 text-sm">{repayment.payment_method}</td>
                            <td data-label="Description" className="px-3 py-2 text-sm text-gray-600">{repayment.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-4">
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedLoanId(null);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </div>
          ) : null}
        </Modal>
      </div>
    </div>
    </PermissionPage>
  );
};

export default LoanArrearsPage;
